import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ArrowSquareOut } from '@phosphor-icons/react';
import { ConfirmDialog } from './ConfirmDialog';
import { createPublicationSnapshot } from './PublicationHistory';

const monoFont = "'IBM Plex Mono', monospace";

interface CmsTopbarProps {
  breadcrumb: string[];
  projectId: string | null;
  projectPublished: boolean | null;
  onProjectStatusChange: () => void;
}

export function CmsTopbar({
  breadcrumb,
  projectId,
  projectPublished,
  onProjectStatusChange,
}: CmsTopbarProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handleTogglePublish = async () => {
    if (!projectId) return;
    setIsPublishing(true);
    
    // Get project name for snapshot description
    const { data: projectData } = await supabase
      .from('projects')
      .select('name')
      .eq('id', projectId)
      .single();
    
    const newPublished = !projectPublished;
    await supabase
      .from('projects')
      .update({ published: newPublished })
      .eq('id', projectId);

    // Create snapshot on publish
    if (newPublished && projectData) {
      await createPublicationSnapshot(
        `${projectData.name} publicado`,
        'project_publish'
      );
    }

    setIsPublishing(false);
    onProjectStatusChange();
    window.dispatchEvent(new CustomEvent('cms-projects-updated'));
  };

  return (
    <>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={projectPublished ? 'Despublicar projeto' : 'Publicar projeto'}
        description={
          projectPublished
            ? 'O projeto ficará oculto no site público. Deseja continuar?'
            : 'O projeto ficará visível no site público. Deseja continuar?'
        }
        confirmLabel={projectPublished ? 'Despublicar' : 'Publicar'}
        cancelLabel="Cancelar"
        onConfirm={handleTogglePublish}
        destructive={projectPublished}
      />

      <header
        className="flex-none flex items-center justify-between"
        style={{
          height: 56,
          backgroundColor: 'rgba(247,246,242,0.06)',
          borderBottom: '0.5px solid rgba(247,246,242,0.08)',
          padding: '0 24px',
        }}
      >
        {/* Breadcrumb */}
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 13,
            fontWeight: 300,
            color: 'rgba(247,246,242,0.5)',
          }}
        >
          {breadcrumb.map((item, i) => (
            <span key={i}>
              {i > 0 && <span style={{ margin: '0 8px' }}>/</span>}
              <span style={{ color: i === breadcrumb.length - 1 ? '#F7F6F2' : undefined }}>
                {item}
              </span>
            </span>
          ))}
        </div>

        {/* Right side actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {/* Publish/Unpublish button */}
          {projectId && projectPublished !== null && (
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={isPublishing}
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                fontWeight: 300,
                textTransform: 'uppercase',
                padding: '8px 16px',
                border: projectPublished
                  ? '1px solid rgba(247,246,242,0.3)'
                  : '1px solid #00E87A',
                backgroundColor: 'transparent',
                color: projectPublished ? 'rgba(247,246,242,0.5)' : '#00E87A',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                opacity: isPublishing ? 0.5 : 1,
              }}
              onMouseEnter={e => {
                if (!isPublishing) {
                  e.currentTarget.style.backgroundColor = projectPublished
                    ? 'rgba(247,246,242,0.05)'
                    : 'rgba(74, 222, 128, 0.1)';
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {projectPublished ? 'Despublicar' : 'Publicar'}
            </button>
          )}

          {/* Visualizar site */}
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: monoFont,
              fontSize: 13,
              fontWeight: 300,
              color: 'rgba(247,246,242,0.4)',
              textDecoration: 'none',
              transition: 'color 0.15s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.9)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(247,246,242,0.4)')}
          >
            <ArrowSquareOut size={14} weight="thin" />
            Visualizar site
          </a>
        </div>
      </header>
    </>
  );
}
